//@ts-check
import { useState } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Drawer,
  Box,
  Collapse,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material'; // Icons for expanding and collapsing
import { BrowserRouter as Router, Switch, Route, Link } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import EditOrganizationViaSalesModal from './modals/EditOrganizationViaSalesModal';
import UserManagement from '../user_management/UserManagement';
import Notes from '../notes/Notes';
import EditBaseConfigurationTab from './edit_base_configuration/EditBaseConfigurationTab';
import CompassProApplication from '../compass_pro_application/CompassProApplication';
import Details from './Details';
import Loading from '../../../components/loading/Loading';
import LoadingData from '../../../components/loading/LoadingData';
import ConfirmModal from '../../../components/modals/utils/ConfirmModal';
import { useSafeguardAdminSignalRContext } from '../../../context_providers/signal_r_context/SafeguardAdminSignalRListener';
import { useUserContext } from '../../../context_providers/user_context/UserProvider';
import {
  useToast,
  ToastType,
} from '../../../context_providers/toast_context/ToastContext';
import { deleteOrganization } from '../../../repository/OrganizationDAO';
import COLORS from '../../../resources/style/Themes';
import { useHistory } from 'react-router-dom';

const drawerWidth = 240;

const OrganizationDetails = () => {
  const { organization } = useSafeguardAdminSignalRContext();
  const [showEditOrganizationModal, setShowEditOrganizationModal] =
    useState(false);
  const [activeTab, setActiveTab] = useState('Overview');
  const [openGroups, setOpenGroups] = useState(false);
  const { isSafeguardDev, bearerToken } = useUserContext();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');
  const history = useHistory();
  const { showToast } = useToast();
  const [selectedGroup, setSelectedGroup] = useState('Default');

  const confirmDeleteOrganization = async () => {
    setShowConfirmModal(false);
    const orgToDelete = {
      org_code: organization.org_code,
      organization_azure_id: organization.id,
    };
    try {
      await deleteOrganization(orgToDelete);

      showToast(
        `Successfully deleted ${organization.company_name}.`,
        ToastType.SUCCESS,
      );
      history.push(`/`);
    } catch (error) {
      showToast(
        `Failed to delete ${organization.company_name}. Please try again.`,
        ToastType.DANGER,
      );
    }
  };

  const handleGroupsClick = () => {
    setOpenGroups(!openGroups);
  };

  const renderData = () => {
    return (
      <Router>
        <Box sx={{ display: 'flex' }}>
          <Drawer
            variant="permanent"
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              [`& .MuiDrawer-paper`]: {
                width: 240,
                boxSizing: 'border-box',
                top: 65,
                height: 'calc(100vh - 65px)',
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: '#3e5371',
                textAlign: 'center',
                color: COLORS.WHITE,
                padding: '10px',
                fontWeight: 'bold',
              }}
            >
              {organization.company_name}
            </Box>
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/organization"
                  selected={activeTab === 'Overview'}
                  onClick={() => setActiveTab('Overview')}
                >
                  <ListItemText primary="Overview" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={handleGroupsClick}>
                  <ListItemText primary="Groups" />
                  {openGroups ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
              </ListItem>
              <Collapse in={openGroups} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {organization.groups &&
                    Object.keys(organization.groups).map((groupName) => (
                      <ListItemButton
                        key={groupName}
                        component={Link}
                        to={`/organization/groups/${encodeURIComponent(groupName)}`}
                        sx={{ pl: 4 }}
                        selected={activeTab === groupName}
                        onClick={() => {
                          setActiveTab(groupName);
                          setSelectedGroup(groupName);
                        }}
                      >
                        <ListItemText primary={groupName} />
                      </ListItemButton>
                    ))}
                </List>
              </Collapse>
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/organization/notes"
                  selected={activeTab === 'Notes'}
                  onClick={() => setActiveTab('Notes')}
                >
                  <ListItemText primary="Notes" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/organization/edit-base-configuration"
                  selected={activeTab === 'EditBaseConfiguration'}
                  onClick={() => setActiveTab('EditBaseConfiguration')}
                >
                  <ListItemText primary="Edit Base Configuration" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/organization/compass-pro"
                  selected={activeTab === 'Shipment Document'}
                  onClick={() => setActiveTab('Shipment Document')}
                >
                  <ListItemText primary="Shipment Document" />
                </ListItemButton>
              </ListItem>
              {isSafeguardDev && (
                <ListItem disablePadding sx={{ background: COLORS.PINK }}>
                  <ListItemButton
                    onClick={() => {
                      setConfirmTitle(`Delete ${organization.company_name}`);
                      setConfirmMessage(
                        `Are you sure you want to delete ${organization.company_name}, organization code ${organization.org_code}? This action cannot be undone!`,
                      );
                      setShowConfirmModal(true);
                    }}
                    sx={{ color: COLORS.DARK_RED }}
                  >
                    <ListItemText
                      primary={`Delete ${organization.company_name}`}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Drawer>

          {/* Main Content */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              bgcolor: 'background.default',
              p: 2,
              minHeight: '100vh',
            }}
          >
            <Switch>
              <Route path="/organization" exact>
                <Details
                  organization={organization}
                  setShowEditOrganizationModal={setShowEditOrganizationModal}
                />

                <EditOrganizationViaSalesModal
                  show={showEditOrganizationModal}
                  onHide={() => setShowEditOrganizationModal(false)}
                  organization={organization}
                />
              </Route>
              <Route path="/organization/groups">
                <UserManagement selectedGroup={selectedGroup} />
              </Route>
              <Route path="/organization/notes">
                <Notes />
              </Route>
              <Route path="/organization/edit-base-configuration">
                <EditBaseConfigurationTab />
              </Route>
              <Route path="/organization/compass-pro">
                <CompassProApplication />
              </Route>
            </Switch>
          </Box>
        </Box>
      </Router>
    );
  };

  return (
    <div>
      {organization ? (
        <>{renderData()}</>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '50vh' }}
        >
          <LoadingData show={true} />
        </div>
      )}
      <ConfirmModal
        show={showConfirmModal}
        confirm={confirmDeleteOrganization}
        onHide={() => setShowConfirmModal(false)}
        confirmMessage={confirmMessage}
        confirmTitle={confirmTitle}
      />
    </div>
  );
};

export default withAuthenticationRequired(OrganizationDetails, {
  onRedirecting: () => <Loading loadingText={'Loading...'} />,
});
